import type { ToolTestResult, ToolTestRun } from '../../lib/api';
import { CopyButton } from './CopyButton';
import styles from './ToolTestResult.module.css';
import { ToolTestSourceResponsePane } from './ToolTestSourceResponsePane';

/** The two outcomes that carry an HTTP response (vs. an engine-level error). */
type ResponseResult = Extract<ToolTestResult, { status: 'success' | 'upstream_http_error' }>;

interface ToolTestResponseCardProps {
  result: ResponseResult;
  sourceResponse?: ToolTestRun['sourceResponse'];
}

export const ToolTestResponseCard = ({ result, sourceResponse }: ToolTestResponseCardProps) => {
  const isSuccess = result.status === 'success';
  const agentText = JSON.stringify(isSuccess ? result.data : result.body, null, 2);
  const sourceText = sourceResponse ? JSON.stringify(sourceResponse.body, null, 2) : undefined;
  // Non-2xx bodies and unmapped successes pass through unchanged — a second identical pane is noise.
  const narrowedSourceText =
    sourceText !== undefined && sourceText !== agentText ? sourceText : undefined;

  const errorBadgeClass =
    !isSuccess && result.httpStatus < 500 ? styles.warningBadge : styles.errorBadge;
  const badge = isSuccess ? (
    <span className={styles.successBadge}>success</span>
  ) : (
    <span className={errorBadgeClass}>HTTP {result.httpStatus}</span>
  );
  const agentPaneTitle = narrowedSourceText !== undefined ? 'What the agent got' : 'Response';

  return (
    <section className={styles.card}>
      <header className={styles.cardHeader}>
        <div className={styles.statusGroup}>
          <span className={styles.cardTitle}>{agentPaneTitle}</span>
          {badge}
        </div>
        <CopyButton value={agentText} />
      </header>
      <pre className={styles.code}>{agentText}</pre>

      {narrowedSourceText !== undefined && sourceResponse && (
        <ToolTestSourceResponsePane sourceResponse={sourceResponse} />
      )}
    </section>
  );
};
